import { useCallback, useEffect, useRef, useState } from 'react';

// Within this distance of the bottom the reader counts as following the conversation.
const PINNED_THRESHOLD_PX = 40;

/**
 * Follows new messages only while the reader is at the bottom of the list;
 * otherwise sets `hasNewBelow` for a "New messages" button instead of yanking
 * them down. Attach `listRef` and `handleScroll` to the scrolling list.
 */
export const useChatScroll = (messages) => {
    const listRef = useRef(null);
    const isPinnedRef = useRef(true);
    const [hasNewBelow, setHasNewBelow] = useState(false);

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        const list = listRef.current;
        if (!list) return;
        list.scrollTo({ top: list.scrollHeight, behavior });
        isPinnedRef.current = true;
        setHasNewBelow(false);
    }, []);

    useEffect(() => {
        if (messages.length === 0) return;
        if (isPinnedRef.current) {
            scrollToBottom();
        } else {
            setHasNewBelow(true);
        }
    }, [messages, scrollToBottom]);

    const handleScroll = () => {
        const list = listRef.current;
        if (!list) return;
        isPinnedRef.current = list.scrollHeight - list.scrollTop - list.clientHeight < PINNED_THRESHOLD_PX;
        if (isPinnedRef.current) setHasNewBelow(false);
    };

    // The user's own message always brings them to the bottom.
    const pinToBottom = () => {
        isPinnedRef.current = true;
    };

    return { listRef, hasNewBelow, scrollToBottom, handleScroll, pinToBottom };
};
